/**
 * Chunk U0 — Multi-document upload queue foundations.
 *
 * Shared model for Invoices & Credit Notes batch upload (sequential review).
 * No UI in U0 — helpers + copy only. Wire in U1+.
 *
 * States: queued → reading → ready → confirming → confirmed | skipped | failed | discarded
 */

/** Soft cap per file-picker selection (monthly plan quota still applies). */
export const UPLOAD_QUEUE_PICK_CAP = 5

/**
 * Max concurrent OCR jobs in the pool.
 * Keep at 1: a sibling reading at the same time can starve the other request
 * (OpenAI 429 / gateway timeout), so only one invoice in a batch gets processed.
 * The pool still accepts a higher override in tests.
 */
export const UPLOAD_QUEUE_OCR_CONCURRENCY = 1

/** Unconfirmed drafts older than this are eligible for cleanup (U5). */
export const UPLOAD_QUEUE_INCOMPLETE_DISCARD_DAYS = 7

export const UPLOAD_DRAFT_STATUS = {
  QUEUED: 'queued',
  READING: 'reading',
  READY: 'ready',
  CONFIRMING: 'confirming',
  CONFIRMED: 'confirmed',
  SKIPPED: 'skipped',
  FAILED: 'failed',
  DISCARDED: 'discarded',
}

/** Terminal statuses — no further OCR / confirm in this session. */
export const UPLOAD_DRAFT_TERMINAL_STATUSES = new Set([
  UPLOAD_DRAFT_STATUS.CONFIRMED,
  UPLOAD_DRAFT_STATUS.SKIPPED,
  UPLOAD_DRAFT_STATUS.DISCARDED,
])

/**
 * Allowed transitions. failed → reading supports Retry.
 * skipped → ready supports Resume from Incomplete tray.
 */
export const UPLOAD_DRAFT_TRANSITIONS = {
  [UPLOAD_DRAFT_STATUS.QUEUED]: [
    UPLOAD_DRAFT_STATUS.READING,
    UPLOAD_DRAFT_STATUS.DISCARDED,
  ],
  [UPLOAD_DRAFT_STATUS.READING]: [
    UPLOAD_DRAFT_STATUS.READY,
    UPLOAD_DRAFT_STATUS.FAILED,
    UPLOAD_DRAFT_STATUS.DISCARDED,
  ],
  [UPLOAD_DRAFT_STATUS.READY]: [
    UPLOAD_DRAFT_STATUS.CONFIRMING,
    UPLOAD_DRAFT_STATUS.SKIPPED,
    UPLOAD_DRAFT_STATUS.DISCARDED,
  ],
  [UPLOAD_DRAFT_STATUS.CONFIRMING]: [
    UPLOAD_DRAFT_STATUS.CONFIRMED,
    UPLOAD_DRAFT_STATUS.READY,
    UPLOAD_DRAFT_STATUS.FAILED,
  ],
  [UPLOAD_DRAFT_STATUS.FAILED]: [
    UPLOAD_DRAFT_STATUS.READING,
    UPLOAD_DRAFT_STATUS.DISCARDED,
  ],
  [UPLOAD_DRAFT_STATUS.SKIPPED]: [
    UPLOAD_DRAFT_STATUS.READY,
    UPLOAD_DRAFT_STATUS.DISCARDED,
  ],
  [UPLOAD_DRAFT_STATUS.CONFIRMED]: [],
  [UPLOAD_DRAFT_STATUS.DISCARDED]: [],
}

export const UPLOAD_QUEUE_COPY = {
  pickCapTitle: 'Too many files',
  pickCapBody: count =>
    `You can select up to ${count} files at a time. Finish this queue, then add another batch.`,
  pickCapOk: 'Got it',

  quotaBlockTitle: 'Upload limit reached',
  quotaBlockBody: remaining =>
    remaining <= 0
      ? 'You’ve used this month’s invoice uploads. OMS Lite includes 5/month, Starter 25, Growth 100, and Pro is unlimited — upgrade your plan, or wait until next month.'
      : `You can upload ${remaining} more this month (Lite 5 · Starter 25 · Growth 100 · Pro unlimited). Select that many or fewer, or upgrade.`,
  /** Explicit offer when pick size > remaining quota (never silent partial). */
  quotaSelectFirstBody: (selected, remaining) =>
    `You selected ${selected} files, but you can upload ${remaining} more this month (Lite 5 · Starter 25 · Growth 100 · Pro unlimited). Use the first ${remaining} now, or go back and choose fewer.`,
  quotaSelectFirstOk: remaining =>
    remaining === 1 ? 'Use the first file' : `Use first ${remaining}`,
  quotaBlockOk: 'Choose fewer files',
  quotaBlockUpgrade: 'View plans',

  reviewLater: 'Review later',
  reviewLaterHint:
    'We’ll keep unfinished uploads here so you can finish them anytime.',
  discardRemaining: 'Discard remaining',
  discardRemainingConfirm:
    'Remove all uploads you haven’t confirmed? Confirmed documents stay in your library.',
  discardRemainingOk: 'Discard remaining',
  discardRemainingCancel: 'Keep them',

  confirmThis: 'Confirm',
  confirmAll: count =>
    count === 1 ? 'Confirm upload' : `Confirm all (${count})`,
  confirmAllHint:
    'Review each file in the list, then save every upload in this batch together.',
  removeThis: 'Remove',

  progressLine: ({ reading, total, ready }) => {
    const t = Number(total) || 0
    const r = Number(reading) || 0
    const readyCount = Number(ready) || 0
    if (t <= 0) return ''
    if (r > 0) {
      return `Reading ${r} of ${t}… · ${readyCount} ready to review`
    }
    if (readyCount > 0) {
      return `${readyCount} ready to review · ${t} in this batch`
    }
    return `${t} in this batch`
  },

  resumeTrayLabel: count =>
    `${count} upload${count === 1 ? '' : 's'} need${
      count === 1 ? 's' : ''
    } review`,
  resumeTrayEmpty: 'No incomplete uploads',

  incompleteDiscardDays: days =>
    `Unconfirmed uploads are removed after ${days} days so your inbox stays clean.`,
  incompleteDiscardedBanner: count =>
    `We cleared ${count} expired incomplete upload${count === 1 ? '' : 's'}.`,

  cnInQueueHint:
    'Credit notes reduce what you owe on a PO. Confirm the details, then apply to the purchase order — don’t create a new order.',
  checkDocumentType:
    'We weren’t sure of the document type — check Invoice vs Credit Note before confirming.',
  checkDocumentTypeAmberLabel: 'Check type',

  allDoneTitle: 'All caught up',
  allDoneBody: 'Every file in this batch is confirmed or removed.',

  ocrFailed: 'Couldn’t read this file — retry, or enter details manually.',
  ocrSparse:
    'We couldn’t read much from this file — fill in the details below, retry reading, or remove it.',
  ocrRetry: 'Retry reading',
}

function normaliseStatus(status) {
  return String(status || '').trim()
}

export function isUploadDraftTerminal(status) {
  return UPLOAD_DRAFT_TERMINAL_STATUSES.has(normaliseStatus(status))
}

export function canTransitionUploadDraft(fromStatus, toStatus) {
  const from = normaliseStatus(fromStatus)
  const to = normaliseStatus(toStatus)
  const allowed = UPLOAD_DRAFT_TRANSITIONS[from]
  if (!allowed) return false
  return allowed.includes(to)
}

/**
 * @param {{
 *   id: string,
 *   pickIndex: number,
 *   filename?: string,
 *   documentType?: string,
 * }} input
 */
export function createUploadDraft({
  id,
  pickIndex,
  filename = '',
  documentType = 'invoice',
} = {}) {
  if (!id && id !== 0) {
    throw new Error('createUploadDraft requires id')
  }
  const index = Number(pickIndex)
  if (!Number.isFinite(index) || index < 0) {
    throw new Error('createUploadDraft requires pickIndex >= 0')
  }
  return {
    id: String(id),
    pickIndex: index,
    filename: String(filename || ''),
    documentType: documentType === 'creditNote' ? 'creditNote' : 'invoice',
    status: UPLOAD_DRAFT_STATUS.QUEUED,
    error: null,
    createdAt: null,
  }
}

/**
 * @param {object[]} files - picker File-like objects ({ name } at minimum)
 * @param {{ createId?: (file, index) => string }} [options]
 */
export function createUploadSessionDrafts(files = [], { createId } = {}) {
  const list = Array.isArray(files) ? files : []
  return list.map((file, index) =>
    createUploadDraft({
      id:
        typeof createId === 'function'
          ? createId(file, index)
          : `local-${index}-${file?.name || 'file'}`,
      pickIndex: index,
      filename: file?.name || `File ${index + 1}`,
    }),
  )
}

export function transitionUploadDraft(draft, toStatus, patch = {}) {
  if (!draft) {
    throw new Error('transitionUploadDraft requires draft')
  }
  const next = normaliseStatus(toStatus)
  if (!canTransitionUploadDraft(draft.status, next)) {
    throw new Error(
      `Invalid upload draft transition: ${draft.status} → ${next}`,
    )
  }
  return {
    ...draft,
    ...patch,
    status: next,
    error:
      next === UPLOAD_DRAFT_STATUS.FAILED
        ? patch.error || draft.error || UPLOAD_QUEUE_COPY.ocrFailed
        : next === UPLOAD_DRAFT_STATUS.READY ||
          next === UPLOAD_DRAFT_STATUS.READING
        ? null
        : patch.error !== undefined
        ? patch.error
        : draft.error,
  }
}

export function countDraftsByStatus(drafts = [], status) {
  const target = normaliseStatus(status)
  return (drafts || []).filter(d => normaliseStatus(d?.status) === target)
    .length
}

export function countDraftsReading(drafts = []) {
  return countDraftsByStatus(drafts, UPLOAD_DRAFT_STATUS.READING)
}

export function countDraftsReady(drafts = []) {
  return countDraftsByStatus(drafts, UPLOAD_DRAFT_STATUS.READY)
}

export function getUploadQueueProgress(drafts = []) {
  const list = drafts || []
  const inBatch = list.filter(
    d => normaliseStatus(d?.status) !== UPLOAD_DRAFT_STATUS.DISCARDED,
  )
  const queued = countDraftsByStatus(list, UPLOAD_DRAFT_STATUS.QUEUED)
  const reading = countDraftsReading(list)
  const ready = countDraftsReady(list)
  // Files that have left the queue (for “Reading N of total…” copy).
  const started = Math.max(0, inBatch.length - queued)
  return {
    total: inBatch.length,
    reading,
    started,
    ready,
    confirmed: countDraftsByStatus(list, UPLOAD_DRAFT_STATUS.CONFIRMED),
    failed: countDraftsByStatus(list, UPLOAD_DRAFT_STATUS.FAILED),
    skipped: countDraftsByStatus(list, UPLOAD_DRAFT_STATUS.SKIPPED),
    queued,
    openForReview: inBatch.filter(
      d =>
        !isUploadDraftTerminal(d?.status) ||
        normaliseStatus(d?.status) === UPLOAD_DRAFT_STATUS.SKIPPED ||
        normaliseStatus(d?.status) === UPLOAD_DRAFT_STATUS.FAILED,
    ).length,
  }
}

export function formatUploadQueueProgressLine(drafts = []) {
  const { total, started, ready, reading } = getUploadQueueProgress(drafts)
  const progressN = started > 0 ? started : reading
  return UPLOAD_QUEUE_COPY.progressLine({ reading: progressN, total, ready })
}

/**
 * How many OCR slots are free given current READING count.
 */
export function getAvailableOcrSlots(
  drafts = [],
  concurrency = UPLOAD_QUEUE_OCR_CONCURRENCY,
) {
  const max = Math.max(0, Number(concurrency) || 0)
  const reading = countDraftsReading(drafts)
  return Math.max(0, max - reading)
}

/**
 * Next QUEUED drafts to start OCR (pick order), limited by free slots.
 */
export function selectNextOcrDrafts(
  drafts = [],
  concurrency = UPLOAD_QUEUE_OCR_CONCURRENCY,
) {
  const slots = getAvailableOcrSlots(drafts, concurrency)
  if (slots <= 0) return []
  return sortUploadDraftsByPickOrder(drafts)
    .filter(d => normaliseStatus(d?.status) === UPLOAD_DRAFT_STATUS.QUEUED)
    .slice(0, slots)
}

/**
 * Ready-first focus: prefer READY (lowest pickIndex), else CONFIRMING,
 * else first non-terminal in pick order.
 */
export function pickReadyFirstFocusId(drafts = [], currentFocusId = null) {
  const list = Array.isArray(drafts) ? drafts.filter(Boolean) : []
  if (!list.length) return null

  const byPick = sortUploadDraftsByPickOrder(list)
  const ready = byPick.filter(
    d => normaliseStatus(d.status) === UPLOAD_DRAFT_STATUS.READY,
  )
  if (ready.length) {
    if (
      currentFocusId &&
      ready.some(d => String(d.id) === String(currentFocusId))
    ) {
      return String(currentFocusId)
    }
    return String(ready[0].id)
  }

  const confirming = byPick.find(
    d => normaliseStatus(d.status) === UPLOAD_DRAFT_STATUS.CONFIRMING,
  )
  if (confirming) return String(confirming.id)

  const next = byPick.find(d => !isUploadDraftTerminal(d.status))
  return next ? String(next.id) : null
}

/** Sidebar: always original pick order. */
export function sortUploadDraftsByPickOrder(drafts = []) {
  return [...(drafts || [])].sort((a, b) => {
    const ai = Number(a?.pickIndex)
    const bi = Number(b?.pickIndex)
    const aSafe = Number.isFinite(ai) ? ai : 0
    const bSafe = Number.isFinite(bi) ? bi : 0
    if (aSafe !== bSafe) return aSafe - bSafe
    return String(a?.id || '').localeCompare(String(b?.id || ''))
  })
}

/** Canonical plan names used by UsageLimits enum. */
export function normalizeUsagePlanName(currentPlan) {
  const raw = String(currentPlan || '').trim()
  if (!raw) return 'Free'
  const key = raw.toLowerCase()
  if (key === 'pro') return 'Pro'
  if (key === 'starter') return 'Starter'
  if (key === 'growth') return 'Growth'
  if (key === 'free') return 'Free'
  return 'Free'
}

/**
 * Paid plan from Stripe / venue.newPricing (Pro > Growth > Starter).
 * Returns null when no paid subscription is active.
 */
export function resolvePlanFromNewPricing(newPricing) {
  const pricing = newPricing || {}
  if (pricing.pro?.isSubscribed) return 'Pro'
  if (pricing.growth?.isSubscribed) return 'Growth'
  if (pricing.starter?.isSubscribed) return 'Starter'
  return null
}

/**
 * Effective plan for invoice UI: Stripe newPricing wins over stale UsageLimits.
 * Matches Venues.js subscription label logic.
 */
export function resolveEffectiveVenuePlan({
  newPricing,
  usageLimits,
  currentPlan,
} = {}) {
  const fromPricing = resolvePlanFromNewPricing(newPricing)
  if (fromPricing) return fromPricing
  return normalizeUsagePlanName(currentPlan ?? usageLimits?.currentPlan)
}

/** Monthly invoice upload cap (`null` = Pro / unlimited). */
export function resolvePlanMonthlyUploadCap(currentPlan) {
  const plan = normalizeUsagePlanName(currentPlan)
  if (plan === 'Pro') return null
  if (plan === 'Starter') return 25
  if (plan === 'Growth') return 100
  return 5
}

/**
 * Remaining quota after a successful unconfirmed-discard restore.
 * Pro / unlimited → null (caller must not write a numeric cap).
 * Never exceeds the plan monthly allowance.
 */
export function remainingQuotaAfterDiscardRestore({
  remainingQuota,
  restoredCount = 0,
  currentPlan,
} = {}) {
  if (remainingQuota == null) return null
  const cap = resolvePlanMonthlyUploadCap(currentPlan)
  if (cap == null) return null
  const restored = Math.max(0, Number(restoredCount) || 0)
  const current = Math.max(0, Number(remainingQuota) || 0)
  return Math.min(cap, current + restored)
}

/**
 * Remaining monthly invoice uploads for preflight.
 * Pro → unlimited (null). Missing feature → plan monthly default (not unlimited).
 * When Stripe plan and UsageLimits.currentPlan disagree, trust the Stripe plan
 * monthly allowance (stale Free caps after upgrade).
 *
 * @param {{
 *   currentPlan?: string|null,
 *   uploadInvoicesDashboard?: { limit?: number }|null,
 *   usageLimitsPlan?: string|null,
 * }} input
 * @returns {number|null}
 */
export function resolveUploadRemainingQuota({
  currentPlan,
  uploadInvoicesDashboard,
  usageLimitsPlan,
} = {}) {
  const plan = normalizeUsagePlanName(currentPlan)
  if (plan === 'Pro') return null

  const limitsPlan =
    usageLimitsPlan != null && String(usageLimitsPlan).trim() !== ''
      ? normalizeUsagePlanName(usageLimitsPlan)
      : plan
  const planMismatch = limitsPlan !== plan

  if (
    !planMismatch &&
    uploadInvoicesDashboard &&
    typeof uploadInvoicesDashboard === 'object'
  ) {
    const limit = Number(uploadInvoicesDashboard.limit)
    if (Number.isFinite(limit)) return Math.max(0, limit)
  }

  return resolvePlanMonthlyUploadCap(plan)
}

/**
 * Take the first N picker files (explicit user choice after quota offer).
 * @param {File[]|FileList|ArrayLike} files
 * @param {number} n
 */
export function takeFirstNPickerFiles(files, n) {
  const list = Array.from(files || [])
  const count = Math.max(0, Number(n) || 0)
  return list.slice(0, count)
}

/**
 * Preflight before upload.
 *
 * @param {{
 *   selectedCount: number,
 *   remainingQuota: number|null|undefined,
 *   pickCap?: number,
 * }} input
 * remainingQuota: null/undefined = unlimited (e.g. Pro)
 */
export function evaluateUploadPickPreflight({
  selectedCount,
  remainingQuota,
  pickCap = UPLOAD_QUEUE_PICK_CAP,
} = {}) {
  const selected = Math.max(0, Number(selectedCount) || 0)
  const cap = Math.max(1, Number(pickCap) || UPLOAD_QUEUE_PICK_CAP)

  if (selected <= 0) {
    return {
      ok: false,
      reason: 'empty',
      allowedCount: 0,
      selectedCount: 0,
      remainingQuota:
        remainingQuota == null
          ? null
          : Math.max(0, Number(remainingQuota) || 0),
      message: '',
    }
  }

  if (selected > cap) {
    return {
      ok: false,
      reason: 'pick_cap',
      allowedCount: cap,
      selectedCount: selected,
      remainingQuota:
        remainingQuota == null
          ? null
          : Math.max(0, Number(remainingQuota) || 0),
      message: UPLOAD_QUEUE_COPY.pickCapBody(cap),
    }
  }

  if (remainingQuota != null && remainingQuota !== undefined) {
    const remaining = Math.max(0, Number(remainingQuota) || 0)
    if (remaining <= 0) {
      return {
        ok: false,
        reason: 'quota_exhausted',
        allowedCount: 0,
        selectedCount: selected,
        remainingQuota: 0,
        message: UPLOAD_QUEUE_COPY.quotaBlockBody(0),
      }
    }
    if (selected > remaining) {
      return {
        ok: false,
        reason: 'quota_exceeded',
        allowedCount: remaining,
        selectedCount: selected,
        remainingQuota: remaining,
        message: UPLOAD_QUEUE_COPY.quotaBlockBody(remaining),
      }
    }
  }

  return {
    ok: true,
    reason: null,
    allowedCount: selected,
    selectedCount: selected,
    remainingQuota:
      remainingQuota == null ? null : Math.max(0, Number(remainingQuota) || 0),
    message: '',
  }
}

/**
 * Whether an incomplete draft is past the discard window (U5).
 */
export function isIncompleteUploadExpired(
  draft = {},
  {
    now = new Date(),
    discardAfterDays = UPLOAD_QUEUE_INCOMPLETE_DISCARD_DAYS,
  } = {},
) {
  const status = normaliseStatus(draft?.status)
  if (
    status === UPLOAD_DRAFT_STATUS.CONFIRMED ||
    status === UPLOAD_DRAFT_STATUS.DISCARDED
  ) {
    return false
  }
  // Only incomplete / in-progress / skipped / failed age out
  const created = draft?.createdAt ? new Date(draft.createdAt) : null
  if (!created || Number.isNaN(created.getTime())) return false
  const days = Math.max(
    1,
    Number(discardAfterDays) || UPLOAD_QUEUE_INCOMPLETE_DISCARD_DAYS,
  )
  const ageMs = new Date(now).getTime() - created.getTime()
  return ageMs >= days * 24 * 60 * 60 * 1000
}

export function filterExpiredIncompleteUploads(drafts = [], options = {}) {
  return (drafts || []).filter(d => isIncompleteUploadExpired(d, options))
}
